// pages/NewsPage.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Shield, ArrowLeft, Clock, User, ChevronRight, Loader2, AlertCircle } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { Button, Card, Badge } from '../components/ui';
import { BASE_URL } from '../constants';
import { getIntlLocale } from '../utils';

export const NewsPage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [news, setNews] = useState([]);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    fetch(`${BASE_URL}/api/news/`)
      .then(r => r.json())
      .then(data => { setNews(data); setStatus('ok'); })
      .catch(() => setStatus('error'));
  }, []);

  const formatDate = (iso) => {
    return new Date(iso).toLocaleDateString(getIntlLocale(i18n.language), {
      day: 'numeric', month: 'long', year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-brand-bg text-gray-700 font-sans">
      {/* Навбар */}
      <nav className="fixed top-0 w-full z-50 bg-brand-bg/80 backdrop-blur-xl border-b border-gray-100 px-12 py-6 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className="bg-brand-accent p-2 rounded-xs text-gray-900 shadow-lg"><Shield size={24} /></div>
          <span className="text-2xl font-black text-gray-900 tracking-tighter uppercase italic leading-none">
            ISV <span className="text-red-600">CENTRAL ASIA</span>
          </span>
        </div>
        <Button variant="ghost" size="md" icon={<ArrowLeft size={14} />} onClick={() => navigate('/')}>
          {t('NewsPage.home')}
        </Button>
      </nav>

      <div className="pt-40 pb-32 px-12 max-w-5xl mx-auto">
        {/* Заголовок */}
        <div className="space-y-4 mb-16">
          <Badge>{t('NewsPage.news_updates')}</Badge>
          <h1 className="text-5xl font-black text-gray-900 uppercase italic tracking-tighter leading-none">
            {t('NewsPage.news_title')}
          </h1>
          <p className="text-gray-400 italic">{t('NewsPage.latest_events_updates_isv_central')}</p>
        </div>

        {/* Состояния */}
        {status === 'loading' && (
          <div className="flex items-center justify-center py-32 text-gray-500">
            <Loader2 size={32} className="animate-spin" />
          </div>
        )}

        {status === 'error' && (
          <div className="flex items-center gap-3 px-6 py-4 bg-brand-danger border border-brand-danger-border rounded-sm text-brand-danger-text">
            <AlertCircle size={18} /> {t('NewsPage.news_load_failed')}
          </div>
        )}

        {status === 'ok' && news.length === 0 && (
          <div className="text-center py-32 text-gray-500 italic">
            {t('NewsPage.no_news_yet')}
          </div>
        )}

        {/* Список новостей */}
        {status === 'ok' && news.length > 0 && (
          <div className="space-y-6">
            {news.map(item => (
              <Card
                key={item.id}
                hover
                size="lg"
                className="rounded-lg group"
                onClick={() => navigate(`/news/${item.short_id}`)}
              >
                {/* Мета */}
                <div className="flex items-center gap-4 mb-5 text-caption font-black uppercase tracking-widest text-gray-500">
                  <span>{formatDate(item.created_at)}</span>
                  <span className="text-gray-700">•</span>
                  <span className="flex items-center gap-1.5">
                    <Clock size={10} /> {item.reading_time} {t('NewsPage.minutes_short')}
                  </span>
                  <span className="text-gray-700">•</span>
                  <span className="flex items-center gap-1.5">
                    <User size={10} /> {item.author}
                  </span>
                </div>

                {/* Заголовок */}
                <h2 className="text-xl font-black text-gray-900 uppercase italic tracking-tight leading-snug mb-4 group-hover:text-red-500 transition-colors">
                  {item.title}
                </h2>

                {/* Анонс */}
                <div className="text-gray-500 text-sm leading-relaxed italic mb-6">
                  <ReactMarkdown components={{ p: ({ children }) => <>{children}</> }}>
                    {item.preview}
                  </ReactMarkdown>
                </div>

                {/* Читать полностью */}
                <div className="flex items-center gap-2 text-red-600 text-caption font-black uppercase tracking-widest group-hover:gap-3 transition-all">
                  {t('NewsPage.read_full')} <ChevronRight size={14} />
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
